import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useState } from "react";
import Typography from "@mui/material/Typography";
import type { CommunityAdmissionResponse } from "@/libs/communityApi";
import {
  useCommunityAdmissions,
  useCommunityInviteCandidates,
} from "./use-community-admissions";

interface CommunityAdmissionReviewProps {
  communityId: string;
  canReview: boolean;
  joinMode: string;
}

/** Owner/moderator review of persisted requests and invitation revocation. */
export function CommunityAdmissionReview({
  communityId,
  canReview,
  joinMode,
}: CommunityAdmissionReviewProps) {
  const { query, approve, decline, revoke, invite } =
    useCommunityAdmissions(communityId);
  const [candidateQuery, setCandidateQuery] = useState("");
  const [selectedCandidate, setSelectedCandidate] = useState<string | null>(
    null,
  );
  const candidates = useCommunityInviteCandidates(
    candidateQuery,
    canReview && joinMode === "invite_only",
  );
  if (!canReview) return null;
  if (query.isLoading) {
    return (
      <Typography color="text.secondary">Loading admission review…</Typography>
    );
  }
  if (query.isError) {
    return (
      <Alert severity="error" role="alert">
        Couldn&apos;t load admission review. Please try again.
      </Alert>
    );
  }
  const rows = query.data?.items ?? [];
  return (
    <Stack
      component="section"
      spacing={1}
      aria-label="Community admission review"
    >
      <Divider />
      <Typography variant="h2" sx={{ fontSize: "1.1rem" }}>
        Admission review
      </Typography>
      {approve.error || decline.error || revoke.error || invite.error ? (
        <Alert severity="error" role="alert">
          Couldn&apos;t update that admission. Please try again.
        </Alert>
      ) : null}
      {joinMode === "invite_only" ? (
        <Stack spacing={1}>
          <TextField
            label="Find a player to invite"
            value={candidateQuery}
            onChange={(event) => {
              setCandidateQuery(event.target.value);
              setSelectedCandidate(null);
            }}
            size="small"
            helperText="Search by public name; only the selected public name is rendered."
          />
          {candidates.isError ? (
            <Alert severity="error" role="alert">
              Player search is unavailable. Please try again.
            </Alert>
          ) : candidates.isFetching ? (
            <Typography color="text.secondary" role="status">
              Searching…
            </Typography>
          ) : null}
          {(candidates.data ?? []).map((candidate) => (
            <Button
              key={candidate.user_id}
              variant={
                selectedCandidate === candidate.user_id
                  ? "contained"
                  : "outlined"
              }
              onClick={() => setSelectedCandidate(candidate.user_id)}
              sx={{ justifyContent: "flex-start" }}
            >
              {candidate.display_name}
            </Button>
          ))}
          <Button
            disabled={!selectedCandidate || invite.isPending}
            onClick={() => {
              if (!selectedCandidate) return;
              invite.mutate(selectedCandidate, {
                onSuccess: () => {
                  setCandidateQuery("");
                  setSelectedCandidate(null);
                },
              });
            }}
            variant="contained"
          >
            {invite.isPending
              ? "Sending invitation…"
              : "Invite selected player"}
          </Button>
        </Stack>
      ) : null}
      {rows.length === 0 ? (
        <Typography color="text.secondary">
          No pending requests or invitations.
        </Typography>
      ) : (
        <List disablePadding>
          {rows.map((admission) => (
            <AdmissionRow
              key={admission.admission_id}
              admission={admission}
              approve={approve}
              decline={decline}
              revoke={revoke}
            />
          ))}
        </List>
      )}
    </Stack>
  );
}

function AdmissionRow({
  admission,
  approve,
  decline,
  revoke,
}: {
  admission: CommunityAdmissionResponse;
  approve: ReturnType<typeof useCommunityAdmissions>["approve"];
  decline: ReturnType<typeof useCommunityAdmissions>["decline"];
  revoke: ReturnType<typeof useCommunityAdmissions>["revoke"];
}) {
  const busy =
    (approve.isPending && approve.variables === admission.admission_id) ||
    (decline.isPending && decline.variables === admission.admission_id) ||
    (revoke.isPending && revoke.variables === admission.admission_id);
  const label = admission.subject_display_name ?? "A player";
  const isRequest = admission.kind === "request";
  return (
    <ListItem
      disableGutters
      secondaryAction={
        <Stack direction="row" spacing={1}>
          {isRequest ? (
            <>
              <Button
                size="small"
                disabled={busy}
                onClick={() => approve.mutate(admission.admission_id)}
              >
                Approve
              </Button>
              <Button
                size="small"
                color="inherit"
                disabled={busy}
                onClick={() => decline.mutate(admission.admission_id)}
              >
                Decline
              </Button>
            </>
          ) : (
            <Button
              size="small"
              color="inherit"
              disabled={busy}
              onClick={() => revoke.mutate(admission.admission_id)}
            >
              Revoke
            </Button>
          )}
        </Stack>
      }
    >
      <ListItemText
        primary={`${isRequest ? "Join request from" : "Invitation to"} ${label}`}
        secondary={
          isRequest ? "Needs owner or moderator approval" : "Pending invitation"
        }
      />
    </ListItem>
  );
}
